import { createContext, type ReactNode, useContext, useMemo } from 'react';

export interface ChatContextValue {
  /**
   * Invoked on each send to prepend extra context (e.g. meeting notes,
   * page content) to the user's message. Return undefined to skip.
   */
  getSendContext?: (threadId?: string) => string | undefined;
  /**
   * Title to set on the chat when the Composer creates it server-side.
   * Lets surfaces bypass backend's auto-rename when they already have a
   * meaningful title (e.g. meeting name).
   */
  chatTitle?: string;
}

const ChatContextCtx = createContext<ChatContextValue>({});

export function ChatContextProvider({
  getSendContext,
  chatTitle,
  children,
}: ChatContextValue & { children: ReactNode }) {
  const value = useMemo(
    () => ({ getSendContext, chatTitle }),
    [getSendContext, chatTitle]
  );
  return (
    <ChatContextCtx.Provider value={value}>{children}</ChatContextCtx.Provider>
  );
}

export function useChatContext(): ChatContextValue {
  return useContext(ChatContextCtx);
}
